import {
  AdvanceState,
  FinanceRequestAction,
  FinanceRequestFormModel,
  FinanceRequestLineModel,
  FinanceRequestStatus,
  FinanceRequestStep,
  FinanceRequestType
} from '../../shared/models/finance-request.model';
import { APP_ROUTES } from '../../shared/config/routes-config';
import { UserRole } from '../../shared/enums/user-role.enum';
import { RouteConfig } from '../../shared/interfaces/routes-config.interface';

export const CONTENT = 'max-w-[1440px] mx-auto px-[clamp(16px,3vw,48px)]';
export const PAGE = 'min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500';
export const SECTION = 'py-[40px]';
export const PAGE_TITLE =
  'm-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500';
export const SECTION_TITLE =
  'm-0 mb-[20px] text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500';
export const CHIP =
  'inline-flex items-center rounded-full px-[10px] py-[3px] text-[12px] font-gilroy-medium whitespace-nowrap';
export const HEADER_CHIP =
  'inline-flex items-center gap-[4px] px-[12px] py-[4px] rounded-full text-[13px] font-gilroy-medium whitespace-nowrap';
export const INPUT =
  'h-[40px] w-full min-w-0 rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';
export const TEXTAREA =
  'min-h-[88px] w-full rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] py-[10px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';
export const LABEL =
  'flex min-w-0 flex-col gap-[6px] text-[12px] font-gilroy-medium text-neutral-300';
export const READONLY =
  'flex min-h-[40px] items-center rounded-[10px] bg-creams-100 px-[12px] text-[13px] text-neutral-500';
export const FIELDS = 'grid gap-[16px] sm:grid-cols-2 lg:grid-cols-4';
export const TABLE_WRAP = 'overflow-x-auto rounded-[12px] border border-gray-200';
export const TH =
  'px-[16px] py-[12px] text-left text-[12px] font-gilroy-medium text-neutral-300 whitespace-nowrap';
export const TD = 'px-[16px] py-[14px] text-[14px] text-neutral-500 align-middle';
export const TD_INPUT = 'px-[6px] py-[6px] align-middle';
export const TFOOT = 'bg-creams-100 text-[14px] font-gilroy-medium text-neutral-500';
export const EMPTY =
  'rounded-[16px] bg-creams-100 px-[24px] py-[32px] text-center text-[15px] text-neutral-300';
export const BTN_PRIMARY =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-primary-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SECONDARY =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] border border-gray-200 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_OUTLINE =
  'inline-flex items-center gap-[8px] px-[20px] py-[12px] rounded-[8px] border border-neutral-500 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:bg-creams-200 disabled:border-gray-200 disabled:text-neutral-100 disabled:cursor-not-allowed';
export const BTN_DANGER =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-scarlet text-neutral-0 text-[14px] font-gilroy-medium cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const LINK =
  'text-[14px] font-gilroy-medium text-neutral-500 underline underline-offset-[3px] cursor-pointer';
export const NOTE_ATTENTION =
  'rounded-[12px] bg-secondary-50 px-[16px] py-[12px] text-[14px] text-secondary-700';
export const NOTE_INFO =
  'rounded-[12px] bg-primary-50 px-[16px] py-[12px] text-[14px] text-primary-700';
export const REMOVE =
  'size-[32px] rounded-full grid place-items-center text-[18px] text-neutral-300 hover:bg-creams-200 hover:text-scarlet cursor-pointer';

const NEUTRAL = 'bg-creams-100 border border-gray-200 text-neutral-500';
const PROGRESS = 'bg-primary-50 text-primary-700';
const ATTENTION = 'bg-secondary-50 text-secondary-700';
const WAITING = 'bg-creams-600 text-creams-900';
const MUTED = 'bg-neutral-50 text-neutral-400';
const LATE = 'bg-secondary-500 text-neutral-0';
const CANCELED = 'bg-scarlet/10 border border-scarlet/20 text-scarlet';
export const CHIP_COLOR = { NEUTRAL, PROGRESS, ATTENTION, WAITING, MUTED, LATE, CANCELED };

export const OVERSIGHT_PERMISSIONS = ['financeRequestsFullAccess', 'financeRequestsReviewAccess'];
export const OVERSIGHT_ROLES: UserRole[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN];

export const TYPES: Record<FinanceRequestType, { name: string; short: string }> = {
  ADV: {
    name: 'Business Advance Request',
    short: 'Advance request'
  },
  STL: {
    name: 'Business Advance Settlement',
    short: 'Advance settlement'
  }
};

export const STATUS: Record<FinanceRequestStatus, { label: string; chip: string }> = {
  draft: { label: 'Draft', chip: NEUTRAL },
  manager: { label: 'With manager', chip: WAITING },
  finance: { label: 'With finance', chip: WAITING },
  approved: { label: 'Approved', chip: PROGRESS },
  returned: { label: 'Returned for changes', chip: ATTENTION },
  rejected: { label: 'Rejected', chip: CANCELED },
  cancelled: { label: 'Withdrawn', chip: MUTED }
};

export const ADVANCE_STATE: Record<AdvanceState, { label: string; chip: string }> = {
  open: { label: 'Awaiting settlement', chip: NEUTRAL },
  settling: { label: 'Settlement in progress', chip: WAITING },
  settled: { label: 'Settled', chip: MUTED }
};

export const STEP_LABEL: Record<FinanceRequestStep, string> = {
  employee: 'Employee',
  manager: 'Line manager',
  finance: 'Finance review'
};

export const ACTION: Record<FinanceRequestAction, { label: string; chip: string }> = {
  submitted: { label: 'Submitted', chip: NEUTRAL },
  resubmitted: { label: 'Resubmitted', chip: NEUTRAL },
  approved: { label: 'Approved', chip: PROGRESS },
  returned: { label: 'Returned for changes', chip: ATTENTION },
  rejected: { label: 'Rejected', chip: CANCELED },
  withdrawn: { label: 'Withdrawn', chip: 'bg-neutral-50 text-neutral-300' }
};

export const CATEGORIES = [
  'Transportation',
  'Meals',
  'Entertainment',
  'Guest & Hospitality',
  'Amenities',
  'Cafeteria Supplies',
  'Office Supplies',
  'Accommodation',
  'Others'
];
export const CURRENCIES = ['EGP', 'USD', 'EUR', 'GBP', 'SAR', 'AED'];
export const PAY_METHODS = ['Cash', 'Bank transfer', 'Other'];
export const RETURN_METHODS = ['', 'Cash', 'Bank transfer', 'Payroll deduction', 'Other'];
export const REIMBURSE_METHODS = ['', 'Cash', 'Bank transfer', 'Payroll', 'Other'];
export const DEFAULT_SETTLE_DAYS = 14;

export const DECLARATION: Record<FinanceRequestType, string> = {
  ADV: 'I confirm that this advance will be used only for the business purpose stated above. I will submit an advance settlement form with receipts by the due date, return any unspent balance, and understand that any unsettled amount may be recovered in line with company policy.',
  STL: 'I confirm the above expenses were incurred solely for business purposes, are supported by the attached receipts, and that any unspent balance will be returned as stated.'
};

const routes = () => APP_ROUTES.FINANCE_REQUESTS_MODULE.CHILDREN as RouteConfig;
export const listPath = (tab?: string) =>
  routes().LIST.FULL_PATH + (tab && tab !== 'mine' ? `?tab=${tab}` : '');
export const newPath = (type: FinanceRequestType, fromAdvance?: string) =>
  routes().NEW.FULL_PATH.replace(':type', type) + (fromAdvance ? `?advance=${fromAdvance}` : '');
export const editPath = (id: string) => routes().EDIT.FULL_PATH.replace(':requestId', id);
export const detailsPath = (id: string) => routes().DETAILS.FULL_PATH.replace(':requestId', id);

const numberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
export const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return isFinite(n) ? n : 0;
};
export const money = (n: number | undefined, currency?: string) =>
  (currency ? `${currency} ` : '') + numberFormat.format(n || 0);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function fmtDate(v?: string | Date | null): string {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v.length === 10 ? `${v}T00:00:00` : v);
  if (isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${String(
    d.getFullYear()
  ).slice(2)}`;
}
export function fmtWhen(v?: Date): string {
  if (!v) return '';
  return `${fmtDate(v)} ${String(v.getHours()).padStart(2, '0')}:${String(v.getMinutes()).padStart(
    2,
    '0'
  )}`;
}
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}
export function addDays(iso?: string, days?: number): string {
  if (!iso || days === undefined || days === null || isNaN(days)) return '';
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

export const blankLine = (): FinanceRequestLineModel => ({ amount: 0, receipt: false });

export function liveTotals(
  type: FinanceRequestType,
  form: FinanceRequestFormModel,
  lines: FinanceRequestLineModel[],
  advanced: number
) {
  const claimed = lines.reduce((a, l) => a + num(l.amount), 0);
  if (type === 'ADV') {
    const requested = num(form.requested);
    const check = !form.requested
      ? ''
      : requested > claimed
      ? 'Requested amount exceeds estimated expenses'
      : num(form.prevOutstanding) > 0
      ? 'Previous advance outstanding - settle before approval'
      : 'OK';
    return { claimed, requested, check, balance: 0, outcome: '' };
  }
  const balance = advanced - claimed;
  const outcome =
    balance > 0
      ? 'Employee returns excess cash to the company'
      : balance < 0
      ? 'Company reimburses employee'
      : 'Fully settled (no balance)';
  return { claimed, balance, outcome, requested: 0, check: '' };
}
